import { DynamicDefaultProps } from '@synergy/utils';
import { InputAdornment, TextField, TextFieldProps, Theme } from '@mui/material';
import { PoolDynamicDefaultPropsComponent } from '@synergy/abstract';
import { ReactNode } from 'react';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

import { NumericTextfield } from './components/numeric-textfield';

const styles = (theme: Theme) =>
    createStyles({
        root: {},
        default: {},
        dense: {
            '& > div > input': {
                paddingRight: '0.9em',
                paddingLeft: '0.9em',
            },
        },
        small: {
            '& > label': {
                display: 'none',
            },
            '& > div > input': {
                paddingTop: 2,
                paddingBottom: 2,
                paddingRight: '0.2em',
                paddingLeft: '0.9em',
                fontSize: '12px',
            },
        },
        medium: {
            '& > label': {
                display: 'none',
            },
            '& > div > input': {
                paddingTop: '0.5em',
                paddingBottom: '0.5em',
                paddingRight: '0.2em',
                paddingLeft: '0.9em',
            },
        },
        fullWidth: {
            display: 'flex',
        },
        readOnly: {
            border: 'none',
            borderRadius: '4px',
            '& > div, input': {
                backgroundColor: theme.palette.background.default,
                opacity: 0.6,
                color: '#21272E',
            },
            '& > label': {
                color: '#21272E',
                opacity: 0.6,
            },
        },
        textOnly: {
            backgroundColor: '#FFF',
            '& > div': {
                border: 'none',
            },
            '& > div > input': {
                paddingRight: '0.9em',
                paddingLeft: '0.9em',
            },
        },
        alignRight: {
            '& > input': {
                textAlign: 'right',
            },
        },
    });

interface PoolTextFieldBaseProps extends WithStyles<typeof styles> {
    fieldType?: keyof ReturnType<typeof styles>;
    isFullWidth?: boolean;
    readOnly?: boolean;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
    noFractionalNumbers?: boolean;
    noNegativeNumbers?: boolean;
    minNumber?: number;
    maxNumber?: number;
    isNumeric?: boolean;
    fixedDecimalScale?: boolean;
    decimalScale?: number;
    align?: 'right' | 'left';
    suffix?: string;
    prefix?: string;
    hasThousandSeparator?: boolean;
    /**
     * Pads the shown value to at least this many decimals once the field is
     * left: 0 reads "0,00", 12.5 reads "12,50", 89.1234 stays. Only what is
     * shown changes, never the value; an empty field stays empty. Amounts,
     * prices and percentages set 2, quantities leave it out (LST-S20, R-269).
     */
    minDecimalScale?: number;
}

export type PoolTextFieldProps = PoolTextFieldBaseProps & TextFieldProps;

export const PoolTextField = withStyles(styles)(
    class PoolTextFieldBase extends PoolDynamicDefaultPropsComponent<PoolTextFieldProps> {
        protected override dynamicDefaultProps: DynamicDefaultProps<PoolTextFieldProps> = {
            default: {
                color: 'primary',
            },
            fullWidth: {
                color: 'primary',
                isFullWidth: true,
            },
        };

        private static getDisplayClass(props: PoolTextFieldProps): string {
            if (props.fieldType) {
                return props.classes[props.fieldType];
            }

            return '';
        }

        private static getClassNames(props: PoolTextFieldProps) {
            return `${props.classes.root} ${this.getDisplayClass(props)} ${props.className || ''} ${
                ((props.disabled || props.readOnly) && props.classes.readOnly) || ''
            }`;
        }

        public override render() {
            const { defaultProps } = this.state;
            const {
                fieldType: _fieldType,
                classes,
                readOnly,
                startAdornment,
                endAdornment,
                noFractionalNumbers,
                noNegativeNumbers,
                minNumber,
                maxNumber,
                isNumeric,
                fixedDecimalScale,
                decimalScale,
                align,
                suffix,
                prefix,
                hasThousandSeparator,
                minDecimalScale,
                ...baseTextFieldProps
            } = this.props;
            const props = { ...defaultProps, ...this.props };

            const useNumericTextfield =
                isNumeric ||
                noFractionalNumbers ||
                noNegativeNumbers ||
                minNumber !== undefined ||
                maxNumber !== undefined ||
                fixedDecimalScale ||
                decimalScale !== undefined ||
                suffix ||
                prefix ||
                hasThousandSeparator ||
                minDecimalScale !== undefined;
            return (
                <TextField
                    {...defaultProps}
                    {...baseTextFieldProps}
                    inputProps={{
                        tabIndex: ((readOnly || this.props.disabled) && -1) || undefined,
                    }}
                    InputProps={{
                        readOnly,
                        startAdornment: startAdornment && (
                            <InputAdornment disableTypography={true} position="start">
                                {startAdornment}
                            </InputAdornment>
                        ),
                        endAdornment: endAdornment && (
                            <InputAdornment disableTypography={true} position="end">
                                {endAdornment}
                            </InputAdornment>
                        ),
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        inputComponent: useNumericTextfield ? (NumericTextfield as any) : undefined,
                        inputProps: useNumericTextfield
                            ? {
                                  noFractionalNumbers,
                                  noNegativeNumbers,
                                  fixedDecimalScale,
                                  decimalScale,
                                  suffix,
                                  prefix,
                                  hasThousandSeparator,
                                  minDecimalScale,
                              }
                            : undefined,
                        className: align === 'right' ? classes.alignRight : '',
                    }}
                    className={PoolTextFieldBase.getClassNames(props)}
                    disabled={this.props.disabled || (readOnly && !this.props.value)}
                    type={this.props.type}
                    onChange={this.props.onChange}
                />
            );
        }
    }
);
